import React, { useState, useEffect } from 'react';
import {
    Modal,
    Backdrop,
    Fade,
    Box,
    makeStyles,
} from '@material-ui/core';
import fontFamily from '../../../../assets/css/fonts';
import color from '../../../../assets/css/colors';
import Button from '../../../../components/Buttons/Button'
import Table, { columnType } from '../../../../components/ClearingTable/Table';
import {
    CustomBodyRenderCurrencyLeft,
    CustomBodyRenderBoolean,
    CustomBodyRenderAlign,
} from '../../../../components/Table/CustomBodyRender';
import { listRequestAudit } from '../../../../services/AchWireRequestService';
import { formatPbDate } from '../../../../lib/fmt';
import {
    protoTimeSpanObjectToString,
} from '../../../../services/ConvertService';

const useStyles = makeStyles(() => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        maxWidth: 800,
        width: '100%',
        margin: '0 auto',
        float: 'clear',
    },
    modalHeader: {
        backgroundColor: 'white',
        padding: '30px 0px 0px 30px',
        borderTopLeftRadius: 10,
        borderTopRightRadius: 10,
        display: 'flex',
        '& > div:nth-child(1)': {
            flex: 'none',
            marginRight: 7,
            '& > svg': {
                width: 28,
                height: 28,
                '& path': {},
            },
        },
        '& div:nth-child(2)': {
            flex: 1,
        },
        '& div:nth-child(3)': {
            flex: 'none',
            '& .MuiIconButton-root': {
                borderRadius: 5,
                padding: 10,
                '&:hover svg path': {},
            },
            '& svg:last-child': {
                width: 15,
                height: 15,
            },
        },
    },
    modalBody: {
        backgroundColor: 'white',
        padding: '0px 10px',
        maxHeight: 600,
        overflowY: 'auto',
        fontFamily: fontFamily.headers,
        color: color.default.main,
        '& p': {
            fontSize: 16,
            fontFamily: fontFamily.headers,
            fontWeight: 400,
            lineHeight: '26px',
            color: color.default.main,
            marginTop: 0,
        },
        '& hr': {
            margin: '30px 0px',
        },
    },
    modalFooter: {
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        backgroundColor: 'white',
        padding: '0px 25px 25px 25px',
        borderBottomLeftRadius: 10,
        borderBottomRightRadius: 10,
        position: 'relative',
        '& button': {
            width: 120,
        },
    },
    incompleteSteps: {
        fontSize: 16,
        lineHeight: '24px',
        marginTop: 20,
        '& > label': {
            fontWeight: 600,
            marginBottom: 10,
            display: 'block',
        },
        '& ul': {
            listStyle: 'none',
            margin: '0px 0px 0px -40px',
            lineHeight: '30px',
            '& > li > span': {
                position: 'relative',
                top: 4,
                '& > svg': {
                    width: 20,
                    height: 20,
                },
            },
        },
    },
    success: {
        '& > svg > path': {},
    },
    error: {
        '& > svg > path': {},
    },
    closeButton: {
        '& button': {
            marginTop: '-10px !important',
            marginRight: '5px !important',
        },
    },
}));

export default function ACHWireAuditModal({
    onClose: handleClose,
    open: isOpen,
    requestId,
}) {
    const classes = useStyles();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const getRequirementDetails = async () => {
            try {
                setLoading(true)
                const data = await listRequestAudit(requestId);
                let rowsCopy = data.requestList;
                const rowsCopyOutput = rowsCopy.map((data) => {

                    data.createdAt = data.createdAt
                        ? protoTimeSpanObjectToString(data.createdAt, 'MM/DD/YYYY hh:mm:ss a')
                        : '--';
                    data.modifiedAt = data.modifiedAt
                        ? protoTimeSpanObjectToString(data.modifiedAt, 'MM/DD/YYYY hh:mm:ss a')
                        : '--';

                    let tempDate2 = formatPbDate(data.systemDate);
                    data.systemDate = tempDate2

                    return data;
                })

                setRows(rowsCopyOutput);
            } catch (error) {
                console.error(error);
            }
            setLoading(false)
        }
        getRequirementDetails();

    }, [isOpen]);


    const columns = [
        {
            name: 'correspondent',
            label: 'Correspondent',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            // Already converted to string in handlesearch
            name: 'systemDate',
            label: 'System Date',
        },
        {
            name: 'accountId',
            label: 'Account ID',
            options: {
                customBodyRender: CustomBodyRenderAlign,
                display: false
            }
        },
        {
            name: 'branch',
            label: 'Branch',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'accountNo',
            label: 'Account No',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'masterAccountNo',
            label: 'Master Account No',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'rep',
            label: 'Rep',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'bankId',
            label: 'Bank ID',
            options: {
                customBodyRender: CustomBodyRenderAlign,
                display: false,
            }
        },
        {
            name: 'walletId',
            label: 'Wallet ID',
            options: {
                customBodyRender: CustomBodyRenderAlign,
                display: false,
            }
        },
        {
            name: 'bankAccountNo',
            label: 'Bank Account No',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'bankRoutingNo',
            label: 'Bank Routing No',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'bankName',
            label: 'Bank Name',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'requestType',
            label: 'Request Type',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'transferType',
            label: 'Transfer Type',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'amt',
            label: 'Amount',
            options: {
                customBodyRender: CustomBodyRenderCurrencyLeft,
            },
            type: 'amount',
        },
        {
            name: 'fee',
            label: 'Fee',
            options: {
                customBodyRender: CustomBodyRenderCurrencyLeft,
            },
            type: 'amount',
        },
        {
            name: 'fedNo',
            label: 'Fed No',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'externalId',
            label: 'External ID',
            options: {
                customBodyRender: CustomBodyRenderAlign,
                visible: false,
            }
        },
        {
            name: 'bank',
            label: 'Bank',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'bankNote',
            label: 'Bank Note',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'status',
            label: 'Status',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            name: 'createdBy',
            label: 'Created By',
            options: {
                customBodyRender: CustomBodyRenderAlign,
            }
        },
        {
            // Already converted to string in handlesearch
            name: 'createdAt',
            label: 'Created At',
        },
        {
            name: 'isInternational',
            label: 'Is International',
            options: {
                customBodyRender: CustomBodyRenderBoolean,
            },
            type: columnType.bool,
        },
        {
            // Already converted to string in handlesearch
            name: 'modifiedAt',
            label: 'Modified At',
        },
        {
            // Already converted to string in handlesearch
            name: 'modifiedBy',
            label: 'Modified By',
        },
        {
            name: 'a-selectAll',
            label: 'Select All',
            options: {
              display: true,
              print: false,
              download: false,
              filter: false,
              setCellHeaderProps: () => ({
                style: {
                  width: '0px',
                  display: 'table-cell',
                  padding: 0,
                  pointerEvents: 'none',
                  fontSize: 0,
                },
              }),
            },
          },
    ];

    const options = {
        selectableRows: 'none',
        responsive: 'standard',
    };

    return (
        <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            className={classes.modal}
            open={isOpen}
            onClose={() => {
                handleClose();
            }}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={isOpen}>
                <div style={{ maxWidth: 1200 }}>
                    <Box component="div" mt={10}>
                        <form noValidate autoComplete="off">
                            <div className={classes.modalHeader}>
                            </div>
                            <div className={classes.modalBody}>
                                <Table
                                    title={'ACH/Wire Audit Logs'}
                                    data={rows}
                                    columns={columns}
                                    options={options}
                                />
                            </div>
                            <div className={classes.modalFooter}>
                                <div
                                    className={classes.grdCellNone}
                                    style={{ marginRight: 10, marginTop: 10 }}
                                >
                                    <Button
                                        color="primary"
                                        label="Close"
                                        onClick={() => {
                                            handleClose();
                                        }}
                                    />
                                </div>
                            </div>
                        </form>
                    </Box>
                </div>
            </Fade>
        </Modal>
    );
}
